import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PIN_SCHEDULE_CRUD, WIDGET_MANAGEMENT_CRUD } from '../../Constants/ApiDefinations/index'
import { DATA_ENGINES } from '../../Constants/ManageWidget'
import {
  IManageWidgetSlice,
  IWidgetInstanceState,
  IPinScheduleDeletePayload,
  IPinScheduleUpsertPayload,
} from '../../Models/ManageWidget'
import mainApiService from '../../Services'
import { showAlert } from './alert.slice'
import { setLoader } from './loader.slice'

const initialState: IManageWidgetSlice = {
  widgetsById: {},
}

const initialWidgetInstanceState: IWidgetInstanceState = {
  widgetDefinitionId: '',
  widgetDefinitionDetails: {},
  widgetDetails: {},
  selectedAssetDetails: [],
  isDirty: false,
  isPinAssetDirty: false,
}

const ensureWidget = (state: IManageWidgetSlice, widgetId: string) => {
  if (!state.widgetsById) {
    state.widgetsById = {}
  }
  if (!state.widgetsById[widgetId]) {
    state.widgetsById[widgetId] = { ...initialWidgetInstanceState }
  }
}

const manageWidgetSlice = createSlice({
  name: 'manageWidget',
  initialState,
  reducers: {
    setWidgetDefinitionId: (
      state,
      action: PayloadAction<{ widgetId: string; definitionId: string }>,
    ) => {
      const { widgetId, definitionId } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].widgetDefinitionId = definitionId
    },
    setIsDirty: (state, action: PayloadAction<{ widgetId: string; value: boolean }>) => {
      const { widgetId, value } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].isDirty = value
    },
    setIsPinAssetDirty: (state, action: PayloadAction<{ widgetId: string; value: boolean }>) => {
      const { widgetId, value } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].isPinAssetDirty = value
    },
    setWidgetDefinitionDetails: (
      state,
      action: PayloadAction<{ widgetId: string; type: string; value: any }>,
    ) => {
      const { widgetId, type, value } = action.payload
      ensureWidget(state, widgetId)
      if (type === 'add') {
        state.widgetsById[widgetId].widgetDefinitionDetails = { ...value }
      } else {
        state.widgetsById[widgetId].widgetDefinitionDetails = {}
      }
    },
    setWidgetDetails: (
      state,
      action: PayloadAction<{ widgetId: string; type: string; value: any }>,
    ) => {
      const { widgetId, type, value } = action.payload
      ensureWidget(state, widgetId)
      if (type === 'add') {
        state.widgetsById[widgetId].widgetDetails = { ...value }
      } else {
        state.widgetsById[widgetId].widgetDetails = {}
      }
    },
    setSelectedAssetDetails: (
      state,
      action: PayloadAction<{ widgetId: string; assets: any[] }>,
    ) => {
      const { widgetId, assets } = action.payload
      ensureWidget(state, widgetId)
      const existingIds = new Set(
        state.widgetsById[widgetId].selectedAssetDetails?.map((asset) => getIDKey(asset)),
      )
      const updatedAssets = assets.map((asset) => {
        const assetId = getIDKey(asset)
        if (existingIds.has(assetId)) {
          const existingAsset = state.widgetsById[widgetId].selectedAssetDetails.find(
            (a) => getIDKey(a) === assetId,
          )
          existingAsset.position = asset.position
          existingAsset.isScheduled = asset.isScheduled ?? false
          existingAsset.isPinned = asset.isPinned ?? false
          if (asset.availableFrom) {
            existingAsset.availableFrom = asset.availableFrom
          } else {
            delete existingAsset.availableFrom
          }
          if (asset.availableTo) {
            existingAsset.availableTo = asset.availableTo
          } else {
            delete existingAsset.availableTo
          }
          return existingAsset
        }
        return asset
      })
      state.widgetsById[widgetId].selectedAssetDetails = updatedAssets
    },

    replaceSelectedAssetDetails: (
      state,
      action: PayloadAction<{ widgetId: string; assets: any[] }>,
    ) => {
      const { widgetId, assets } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].selectedAssetDetails = assets
    },
    resetWidgetData: (state, action: PayloadAction<string | undefined>) => {
      if (!state.widgetsById) {
        state.widgetsById = {}
      }
      if (action.payload) {
        delete state.widgetsById[action.payload]
      } else {
        state.widgetsById = {}
      }
    },
  },
})
export const {
  replaceSelectedAssetDetails,
  setSelectedAssetDetails,
  setWidgetDefinitionDetails,
  setWidgetDefinitionId,
  setWidgetDetails,
  resetWidgetData,
  setIsDirty,
  setIsPinAssetDirty,
} = manageWidgetSlice.actions

export default manageWidgetSlice

// --- Selectors ---

export const manageWidgetSelector = (state: any) => state.manageWidget

export const getIDKey = (asset: any): string | undefined => {
  return asset?.contentId || asset?.assetId || asset?.containerId || asset?.widgetId || asset?.id
}

export const makeWidgetDefinitionIdSelector = (widgetId: string) =>
  createSelector(
    [(state: any) => state.manageWidget.widgetsById?.[widgetId]],
    (widgetState: IWidgetInstanceState | undefined) => widgetState?.widgetDefinitionId ?? '',
  )

export const makeIsDirtySelector = (widgetId: string) =>
  createSelector(
    [(state: any) => state.manageWidget.widgetsById?.[widgetId]],
    (widgetState: IWidgetInstanceState | undefined) => widgetState?.isDirty ?? false,
  )

export const makeIsPinAssetDirtySelector = (widgetId: string) =>
  createSelector(
    [(state: any) => state.manageWidget.widgetsById?.[widgetId]],
    (widgetState: IWidgetInstanceState | undefined) => widgetState?.isPinAssetDirty ?? false,
  )

export const makeSelectedAssetDetailsSelector = (widgetId: string) =>
  createSelector(
    [(state: any) => state.manageWidget.widgetsById?.[widgetId]],
    (widgetState: IWidgetInstanceState | undefined) => widgetState?.selectedAssetDetails ?? [],
  )

export const makeWidgetDefinitionDataSelector = (widgetId: string) =>
  createSelector(
    [(state: any) => state.manageWidget.widgetsById?.[widgetId]],
    (widgetState: IWidgetInstanceState | undefined) => widgetState?.widgetDefinitionDetails ?? {},
  )

export const makeWidgetDetailsSelector = (widgetId: string) =>
  createSelector(
    [(state: any) => state.manageWidget.widgetsById?.[widgetId]],
    (widgetState: IWidgetInstanceState | undefined) => widgetState?.widgetDetails ?? {},
  )

// --- Async Thunks ---

export const getWidgetListAPICall = createAsyncThunk(
  'manageWidget/widgetList',
  async (payload: any, { dispatch }) => {
    const requestedData: any = WIDGET_MANAGEMENT_CRUD.LISTING(payload)
    const data: any = await mainApiService(requestedData)
    return data
  },
)

export const assetList = createAsyncThunk(
  'manageWidget/assetList',
  async (data: any, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal = WIDGET_MANAGEMENT_CRUD.ASSET_LISTING(data)
    const result = await mainApiService(requestedData)
    return result
  },
)

export const assetTypes = createAsyncThunk(
  'manageWidget/assetTypes',
  async (data: string, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal = WIDGET_MANAGEMENT_CRUD.TYPES_LISTING()
    const result = await mainApiService(requestedData)
    return result
  },
)

export const assetLayouts = createAsyncThunk(
  'manageWidget/assetLayouts',
  async (widgetType: string, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal =
      WIDGET_MANAGEMENT_CRUD.LAYOUTS_LISTING(widgetType)
    const result = await mainApiService(requestedData)
    return result
  },
)

export const getWidgetDefinitionDetailById = createAsyncThunk(
  'manageWidget/getWidgetDefinitionDetailById',
  async ({ id, widgetId }: { id: string; widgetId: string }, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal =
      WIDGET_MANAGEMENT_CRUD.GET_WIDGET_DEFINITION_BY_ID(id)
    const result: any = await mainApiService(requestedData)
    dispatch(setWidgetDefinitionDetails({ widgetId, type: 'add', value: result?.data }))
    return result
  },
)

export const getWidgetDetailById = createAsyncThunk(
  'manageWidget/getWidgetDetailById',
  async ({ id, widgetId }: { id: string; widgetId: string }, { dispatch }) => {
    const requestedData: WidgetCRUDAPIDefinitionModal =
      WIDGET_MANAGEMENT_CRUD.GET_WIDGET_DETAILS_BY_ID(id)
    const result: any = await mainApiService(requestedData)
    const resultData = result?.data
    if (
      resultData?.data?.engine?.toLowerCase() === DATA_ENGINES.automated ||
      resultData?.data?.engine?.toLowerCase() === DATA_ENGINES.recommendation
    ) {
      if (resultData?.data?.query && Object.keys(resultData?.data?.query)?.length > 0) {
        resultData.data.query = Object.entries(resultData.data.query).reduce(
          (acc: any, [key, value]: any) => {
            if (Array.isArray(value)) {
              value = value.map((item: any) => {
                const { parameterId } = item
                if (parameterId && typeof parameterId === 'object') {
                  return {
                    ...item,
                    parameterId: parameterId._id,
                    dataType: parameterId.dataType,
                    fieldType: parameterId.fieldType,
                  }
                }
                return item
              })
            }
            acc[key] = value
            return acc
          },
          {},
        )
      }
    }
    dispatch(setWidgetDetails({ widgetId, type: 'add', value: resultData }))
    return result
  },
)

export const upsertPinSchedule = createAsyncThunk(
  'manageWidget/upsertPinSchedule',
  async (payload: IPinScheduleUpsertPayload, { dispatch }) => {
    dispatch(setLoader(true))
    try {
      const requestedData = PIN_SCHEDULE_CRUD.UPSERT(payload)
      const result = await mainApiService(requestedData)
      if (result?.responseCode !== 200) {
        const errorMessage =
          result?.error?.message || result?.message || 'Failed to update schedule'
        dispatch(showAlert([true, errorMessage, 'error']))
      } else {
        dispatch(showAlert([true, result?.message || 'Schedule updated successfully', 'success']))
      }
      return result
    } catch (error: any) {
      const errorMessage = error?.message || 'An error occurred while updating schedule'
      dispatch(showAlert([true, errorMessage, 'error']))
      throw error
    } finally {
      dispatch(setLoader(false))
    }
  },
)

export const removePinSchedule = createAsyncThunk(
  'manageWidget/removePinSchedule',
  async (payload: IPinScheduleDeletePayload, { dispatch }) => {
    dispatch(setLoader(true))
    try {
      const requestedData = PIN_SCHEDULE_CRUD.REMOVE(payload)
      const result = await mainApiService(requestedData)
      if (result?.responseCode !== 200) {
        const errorMessage =
          result?.error?.message || result?.message || 'Failed to remove schedule'
        dispatch(showAlert([true, errorMessage, 'error']))
      } else {
        dispatch(showAlert([true, result?.message || 'Schedule removed successfully', 'success']))
      }
      return result
    } catch (error: any) {
      const errorMessage = error?.message || 'An error occurred while removing schedule'
      dispatch(showAlert([true, errorMessage, 'error']))
      throw error
    } finally {
      dispatch(setLoader(false))
    }
  },
)
export interface WidgetCRUDAPIDefinitionModal {
  url: string
  method: string
  data: any
}
